import { SyntheticEvent, useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import styled from "styled-components";

import { Attempt } from "../hooks/useAttempts";
import usePrevious from "../hooks/usePrevious";
import { ApiInlineUser } from "../types";
import { getNumberWithOrdinal } from "../utils/getNumberWithOrdinal";

type AttemptsInputProps = {
  maxAttemptsCount?: number;
  attempts?: Attempt[];
  existingAttempts?: Attempt[];
  onChange: (attempts: Attempt[]) => void;
  onTouched?: () => void;
  disabled?: boolean;
  segmentsCount?: number;
  canModify?: boolean;
  judgesByUUID: Record<string, ApiInlineUser>;
  hardMaxAttemptsCount?: number | null;
  isTouched?: boolean;
};

const TryIcon = () => <div className="display-5">−</div>;
const ZoneIcon = () => <div className="display-5">Z</div>;
const Z1Icon = () => <div className="display-5">Z1</div>;
const Z2Icon = () => <div className="display-5">Z2</div>;
const TopIcon = () => <div className="display-5">T</div>;

const AttemptButton = styled(Button).attrs<{
  icon: React.ReactNode;
}>(({ icon, children }) => ({
  children: (
    <>
      {icon}
      {children}
    </>
  ),
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex-basis: 0;
  flex-grow: 1;
  & + & {
    margin-left: 8px;
  }
  font-size: 12px;
  padding: 4px 6px 8px 6px;
  line-height: 1;
  svg {
    margin-bottom: 8px;
  }
  background-color: white;
  border-width: 0;
  &.active {
    font-weight: bold;
  }
`;

const StyledTable = styled(Table)`
  tr.table-active {
    th,
    td {
      background-color: #f7f8f9;
      box-shadow: none;
      border-bottom-color: transparent;
    }
    *:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
    *:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }
`;

const AttemptSquare = ({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant: string;
}) => {
  const color = variant === "primary" ? "white" : "black";
  const bg = variant === "secondary" ? "white" : variant;
  return (
    <div
      className={`d-flex justify-content-center align-items-center rounded-3 bg-${bg} text-${color}`}
      style={{ width: 36, height: 36 }}
    >
      <span style={{ transform: "scale(0.5)" }}>{children}</span>
    </div>
  );
};

const AttemptRow = ({
  rowIndex,
  status,
  canModify,
  attempt,
  onActivate,
  judgesByUUID,
  segmentsCount,
  onUpdateAttempt,
}: {
  rowIndex: number;
  status: "active" | "addable" | "inactive";
  canModify: boolean;
  attempt?: Attempt;
  onActivate: (index: number) => void;
  segmentsCount: number | undefined;
  onUpdateAttempt: (
    index: number,
    success: boolean,
    segmentsCount: number,
  ) => void;
  judgesByUUID: Record<string, ApiInlineUser>;
}) => {
  return (
    <tr
      className={
        status === "active"
          ? "table-active"
          : status === "addable"
          ? "table-addable"
          : ""
      }
      onClick={() => {
        if (status === "active" || !canModify) return;
        onActivate(rowIndex);
      }}
      key={rowIndex}
    >
      <th className="fs-6 w-10 text-left" style={{ width: 50 }}>
        {getNumberWithOrdinal(rowIndex + 1)}
      </th>
      {status === "inactive" ? (
        <td>
          {attempt ? (
            <div>
              <div className="small text-muted">
                {attempt.timestamp.getHours().toString().padStart(2, "0")}:
                {attempt.timestamp.getMinutes().toString().padStart(2, "0")}
              </div>
              {attempt.judge ? (
                <div className="small text-muted">
                  Judge:{" "}
                  {judgesByUUID[attempt.judge]
                    ? judgesByUUID[attempt.judge]?.name
                    : "Staff"}
                </div>
              ) : null}
            </div>
          ) : null}
        </td>
      ) : null}
      <td colSpan={status === "active" || status === "addable" ? 2 : 1}>
        {status === "active" ? (
          <div className="d-flex">
            <AttemptButton
              variant="outline-secondary"
              active={
                attempt && !attempt.success ? !attempt.segmentsCount : null
              }
              icon={<TryIcon />}
              onClick={(e: SyntheticEvent) => {
                onUpdateAttempt(rowIndex, false, 0);
                e.stopPropagation();
              }}
            >
              Try
            </AttemptButton>
            {(segmentsCount || 0) > 0 ? (
              <AttemptButton
                variant="outline-primary"
                active={
                  attempt && !attempt.success
                    ? attempt.segmentsCount === 1
                    : null
                }
                icon={segmentsCount === 1 ? <ZoneIcon /> : <Z1Icon />}
                onClick={(e: SyntheticEvent) => {
                  onUpdateAttempt(rowIndex, false, 1);
                  e.stopPropagation();
                }}
              >
                Zone{segmentsCount === 1 ? "" : " 1"}
              </AttemptButton>
            ) : null}
            {(segmentsCount || 0) > 1 ? (
              <AttemptButton
                variant="outline-primary"
                active={
                  attempt && !attempt.success
                    ? attempt.segmentsCount === 2
                    : null
                }
                icon={<Z2Icon />}
                onClick={(e: SyntheticEvent) => {
                  onUpdateAttempt(rowIndex, false, 2);
                  e.stopPropagation();
                }}
              >
                Zone 2
              </AttemptButton>
            ) : null}
            <AttemptButton
              variant="success"
              active={attempt ? attempt.success : null}
              icon={<TopIcon />}
              onClick={(e: SyntheticEvent) => {
                onUpdateAttempt(rowIndex, true, 1);
                e.stopPropagation();
              }}
            >
              Top
            </AttemptButton>
          </div>
        ) : (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ minHeight: 40 }}
          >
            {attempt ? (
              <div className="d-flex justify-content-end flex-fill">
                {attempt.success ? (
                  <AttemptSquare variant="success">
                    <TopIcon />
                  </AttemptSquare>
                ) : attempt.segmentsCount === 1 ? (
                  <AttemptSquare variant="primary">
                    {segmentsCount === 1 ? <ZoneIcon /> : <Z1Icon />}
                  </AttemptSquare>
                ) : attempt.segmentsCount >= 2 ? (
                  <AttemptSquare variant="primary">
                    <Z2Icon />
                  </AttemptSquare>
                ) : (
                  <AttemptSquare variant="secondary">
                    <TryIcon />
                  </AttemptSquare>
                )}
              </div>
            ) : null}
            {status === "addable" ? (
              <div className="d-flex justify-content-center flex-fill">
                <Button size="sm" variant="outline-secondary">
                  Add attempt {rowIndex + 1}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
};

// Start by opening the next attempt row, unless a top or attempt cap
// means no further edits can be added.
const getInitialActiveIndexes = (
  attempts: Attempt[] | undefined,
  maxAttemptsCount: number,
) => {
  if (!attempts) {
    return [];
  }
  if (attempts.length === 0) {
    return [0];
  }
  if (
    attempts.length === maxAttemptsCount ||
    !!attempts.find((x) => x.success)
  ) {
    return [];
  }
  return [attempts.length];
};

const AttemptsInput = ({
  maxAttemptsCount = 5,
  hardMaxAttemptsCount = null,
  segmentsCount,
  attempts,
  existingAttempts,
  onChange,
  canModify,
  disabled = false,
  onTouched,
  isTouched,
  judgesByUUID,
}: AttemptsInputProps) => {
  const getDisplayCount = () => {
    if (!existingAttempts || !attempts) {
      return maxAttemptsCount;
    }
    if (disabled) {
      return Math.max(existingAttempts.length, maxAttemptsCount);
    }
    if (existingAttempts[existingAttempts.length - 1]?.success) {
      return Math.max(existingAttempts.length, maxAttemptsCount);
    }
    if (attempts.length) {
      return Math.max(
        Math.min(
          attempts.length + (!attempts[attempts.length - 1]?.success ? 1 : 0),
          hardMaxAttemptsCount ?? Infinity,
        ),
        maxAttemptsCount,
      );
    }
    return Math.max(existingAttempts.length + 1, maxAttemptsCount);
  };

  const displayCount = getDisplayCount();
  // These are the row indexes whose edit controls are currently open.
  const [activeAttemptIndexes, setActiveAttemptIndexes] = useState<number[]>(
    getInitialActiveIndexes(attempts, displayCount),
  );

  const wasTouched = usePrevious(isTouched);
  useEffect(() => {
    if (wasTouched && !isTouched) {
      setActiveAttemptIndexes(
        getInitialActiveIndexes(attempts, maxAttemptsCount),
      );
    }
  }, [isTouched, attempts, maxAttemptsCount, wasTouched]);

  const prevAttempts = usePrevious(attempts);
  useEffect(() => {
    if (!prevAttempts && attempts) {
      // Attempts has just loaded, so initialize active indexes based on loaded attempts.
      setActiveAttemptIndexes(getInitialActiveIndexes(attempts, displayCount));
    }
  }, [attempts, prevAttempts, displayCount]);

  const onUpdateAttempt = (
    attemptIndex: number,
    success: boolean,
    segmentsCount_: number,
  ) => {
    if (!attempts || activeAttemptIndexes.length === 0) {
      return;
    }
    let update = [...attempts];
    update[attemptIndex] = {
      ...(update[attemptIndex] || { timestamp: new Date() }),
      segmentsCount: segmentsCount_,
      success: success,
    };
    if (success) {
      // If the attempt was marked as successful, remove active state from all subsequent attempts
      update = update.slice(0, attemptIndex + 1);
      setActiveAttemptIndexes((prev) =>
        prev.filter((index) => index <= attemptIndex),
      );
    }
    onChange(update);
  };

  return (
    <StyledTable className={`align-middle ${disabled ? "pe-none" : ""}`}>
      <thead></thead>
      <tbody>
        {[...Array(displayCount)].map((_, i) => {
          const attempt = attempts?.[i];
          const existingAttempt = existingAttempts?.[i];
          const previousAttempt = attempts?.[i - 1];
          const canModifyAttempt = !existingAttempt || !!canModify;
          // Only the immediate empty row after an unfinished attempt can be opened.
          const isAddableRow =
            activeAttemptIndexes.includes(i - 1) &&
            !attempt &&
            previousAttempt &&
            !previousAttempt.success;

          return (
            <AttemptRow
              key={i}
              rowIndex={i}
              canModify={canModifyAttempt}
              status={
                disabled
                  ? "inactive"
                  : activeAttemptIndexes.includes(i)
                  ? "active"
                  : isAddableRow
                  ? "addable"
                  : "inactive"
              }
              attempt={attempt}
              onActivate={(index) => {
                // Opening the next row does not close earlier rows, so judges can
                // batch-enter several attempts before saving.
                if (!canModifyAttempt) {
                  return;
                }
                // Only allow activating the next attempt if the previous one exists and is not a success.
                if (i > 0 && (!previousAttempt || previousAttempt?.success)) {
                  return;
                }
                setActiveAttemptIndexes((prev) => [...prev, index]);
                onTouched?.();
              }}
              judgesByUUID={judgesByUUID}
              segmentsCount={segmentsCount}
              onUpdateAttempt={onUpdateAttempt}
            />
          );
        })}
      </tbody>
    </StyledTable>
  );
};

export default AttemptsInput;
